<script setup lang="ts">
import { NInput } from 'naive-ui'
import { ref, useAttrs, watch } from 'vue'

const props = defineProps({
  value: {
    type: [Number, null],
    required: false,
    default: null,
  },
  type: {
    type: String,
    required: false,
    default: 'text',
  },
  maxlength: {
    type: Number,
    default: 15,
  },
  precision: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['update:value', 'input', 'change', 'blur', 'clear', 'focus'])

const attrs = useAttrs()
const displayValue = ref(formatNumber(props.value))

watch(
  () => props.value,
  (val) => {
    displayValue.value = formatNumber(val)
  },
)

function formatNumber(value: number | null) {
  if (value === null || Number.isNaN(value))
    return ''
  const fixed = Number(value).toFixed(Math.max(0, props.precision))
  const [intPart, decPart = ''] = fixed?.split('.')
  const intWithSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return props.precision > 0 ? `${intWithSep},${decPart}` : intWithSep
}

function parseInput(raw: string): number | null {
  if (!raw)
    return null
  const normalized = raw.replace(/\./g, '').replace(',', '.')
  const parsed = Number(normalized)
  if (Number.isNaN(parsed))
    return null
  return Number(parsed.toFixed(Math.max(0, props.precision)))
}

const allowInput = (next: string) => {
  if (!next)
    return true
  // strip thousands separators then measure integer part only
  const normalized = next?.replace(/\./g, '')
  const [intPart] = normalized?.split(/[.,]/)
  if (intPart.length > props.maxlength)
    return false
  return /^\d+(\.\d+)?$/.test(intPart)
}

const onUpdate = (v: string) => {
  displayValue.value = v
  const parsed = parseInput(v)
  emit('update:value', parsed)
}

const onInput = (v: string) => {
  displayValue.value = v
  const parsed = parseInput(v)
  emit('input', parsed)
}

const onChange = (v: string) => {
  displayValue.value = v
  const parsed = parseInput(v)
  emit('change', parsed)
}

const onBlur = (e: any) => {
  const raw = e?.target?.value ?? ''
  displayValue.value = formatNumber(parseInput(raw))
  emit('update:value', parseInput(raw))
  emit('blur', e)
}

const onClear = (e: any) => {
  displayValue.value = ''
  emit('update:value', null)
  emit('clear', e)
}

const onFocus = (e: any) => {
  emit('focus', e)
}
</script>

<template>
  <NInput
    :value="displayValue"
    v-bind="attrs"
    type="text"
    :allow-input="allowInput"
    class="custom-input-number"
    @update:value="onUpdate"
    @input="onInput"
    @change="onChange"
    @blur="onBlur"
    @clear="onClear"
    @focus="onFocus"
  >
    <template #prefix>
      <slot name="prefix" />
    </template>
    <template #suffix>
      <slot name="suffix" />
    </template>
  </NInput>
</template>

<style scoped>
</style>
